import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-white">
      <header className="aegis-navy-bg text-white py-10">
        <div className="container-x flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-home-link"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
          <div className="font-display font-semibold">Aegis Care</div>
        </div>
      </header>

      <main className="container-x py-16 max-w-3xl">
        <div className="eyebrow">Legal</div>
        <h1
          data-testid="privacy-heading"
          className="font-display text-3xl md:text-5xl font-semibold mt-3 aegis-navy-text"
        >
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-gray-500">
          Last updated: {new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="prose prose-lg max-w-none mt-10 space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">1. Who we are</h2>
            <p>
              Aegis Care Operations LTD ("Aegis Care", "we", "our") is a home
              care services company operating in Alberta, Canada. We can be
              reached at <a className="text-[#3D7A73] underline" href="mailto:info@aegishomecare.ca">info@aegishomecare.ca</a> or 780-994-6439.
            </p>
            <p className="mt-3 text-sm text-gray-600">
              Business Registration No.: <strong>772874178</strong> &nbsp;|&nbsp; Licence No.: <strong>2028274351</strong>
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">2. What information we collect</h2>
            <p>When you submit our consultation booking form, we collect only the information you voluntarily provide:</p>
            <ul className="list-disc pl-6 mt-3 space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>The service category you are interested in</li>
              <li>Preferred consultation date and time</li>
              <li>Any optional notes you choose to share</li>
            </ul>
            <p className="mt-3">
              <strong>Please do not submit confidential identifiers</strong> such as Alberta Health Care numbers, driver's licence numbers, passport numbers, or financial information through the website form. Sensitive health information will be collected securely during your in-person consultation.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">3. How we use your information</h2>
            <ul className="list-disc pl-6 mt-3 space-y-1">
              <li>To respond to your consultation request and schedule a call.</li>
              <li>To match you with an appropriate caregiver and prepare your care plan.</li>
              <li>To follow up on services you have requested.</li>
            </ul>
            <p className="mt-3">We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">4. How your data is stored</h2>
            <p>
              Form submissions are stored in an access-controlled database hosted on infrastructure managed by our website provider. Data is transmitted to and from our servers over encrypted HTTPS connections. Where email notifications are enabled, your submission is also delivered to our intake inbox via a third-party email service provider (Resend) subject to their privacy practices.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">5. Who can access your information</h2>
            <p>
              Access to consultation submissions is limited to authorized Aegis Care intake staff and the administrators who maintain our website. Caregivers and clinical staff only receive the information necessary to deliver your requested service, on a need-to-know basis.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">6. Data retention</h2>
            <ul className="list-disc pl-6 mt-3 space-y-1">
              <li>If you do not become a client, we retain your contact submission for up to <strong>24 months</strong> for follow-up purposes, after which it is deleted.</li>
              <li>If you become a client, your intake record is retained as part of your care file in accordance with Alberta health-record retention requirements.</li>
              <li>You may request earlier deletion at any time by emailing <a className="text-[#3D7A73] underline" href="mailto:info@aegishomecare.ca">info@aegishomecare.ca</a>.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">7. Your rights</h2>
            <p>
              Under Alberta's Personal Information Protection Act (PIPA), you have the right to access the personal information we hold about you, request correction, and request deletion. To exercise these rights, contact us at <a className="text-[#3D7A73] underline" href="mailto:info@aegishomecare.ca">info@aegishomecare.ca</a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">8. Cookies & analytics</h2>
            <p>
              Our website uses minimal technical cookies required for the site to function and may use anonymous analytics to understand site usage. We do not use advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold aegis-navy-text">9. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The "Last updated" date at the top reflects the most recent revision.
            </p>
          </section>
        </div>

        <div className="mt-14">
          <Link to="/" className="btn-primary" data-testid="privacy-back-cta">
            <ArrowLeft size={16} /> Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}
